import React, { useEffect, useMemo, useState } from "react";
import { sanitizeRequestFalsyToNull } from "../../Utils/sanitizeRequestFalsyToNull";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCreateCustomer } from "../../hooks/api/useCustomer";
import { CustomerType } from "./enums/customerType";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  getDefaultCountryCode,
  HandledGraphQLMutationError,
  translateCustomerCreateConflict
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { UserRole } from "../Profile/enums/profileEnums";
import AddressForm from "../common/AddressForm/AddressForm";
import { CustomerFormData, CreateCustomerInput } from "./types/customer";
import { buildCustomerSchema } from "../../Utils/customerSchema";

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  /** Store from route; company admins submit customers for this store (no store picker). */
  fixedStoreId?: string;
  /** Parent refreshes the list query (e.g. `refetch` from `useGetCustomerList`). */
  onCustomerCreated?: () => void | Promise<unknown>;
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  open,
  onClose,
  fixedStoreId,
  onCustomerCreated
}) => {
  const createCustomer = useCreateCustomer();
  const [listRefreshPending, setListRefreshPending] = useState(false);
  const [submitApiError, setSubmitApiError] = useState<string | null>(null);
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const isCompanyAdmin =
    String(userDetails?.role ?? "").trim() === UserRole.COMPANY_ADMIN;
  const { t } = useKivunovaTranslation();
  const lockedStoreId = String(fixedStoreId ?? "").trim();

  const customerSchema = useMemo(
    () =>
      buildCustomerSchema(t, {
        requireStoreId: isCompanyAdmin && Boolean(lockedStoreId)
      }),
    [t, isCompanyAdmin, lockedStoreId]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue
  } = useForm<CustomerFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(customerSchema as any),
    mode: "onChange",
    defaultValues: {
      companyId: userDetails?.companyId || "",
      storeId: "",
      name: "",
      tin: "",
      phone: "",
      phoneCountryCode: getDefaultCountryCode(),
      email: "",
      type: CustomerType.INDIVIDUAL,
      address: {
        country: "Rwanda",
        province: "",
        district: "",
        sector: "",
        cell: "",
        village: "",
        streetAddress: ""
      }
    }
  });

  useEffect(() => {
    if (!open || !isCompanyAdmin || !lockedStoreId) return;
    setValue("storeId", lockedStoreId);
  }, [open, isCompanyAdmin, lockedStoreId, setValue]);

  useEffect(() => {
    if (open) setSubmitApiError(null);
  }, [open]);

  const customerType = useWatch({ control, name: "type" });

  const onSubmit: SubmitHandler<CustomerFormData> = async (data) => {
    try {
      const trimmedLocal = (data.phone ?? "").trim();
      const fullPhoneNumber = trimmedLocal
        ? combinePhoneNumber(
            data.phoneCountryCode as string,
            trimmedLocal,
            PhoneCountryCodeMap
          )
        : undefined;

      const createCustomerInput = sanitizeRequestFalsyToNull({
        companyId: data.companyId,
        name: data.name,
        tin: data.tin,
        ...(fullPhoneNumber ? { phone: fullPhoneNumber } : {}),
        ...(data.email?.trim() ? { email: data.email.trim() } : {}),
        address: data.address,
        type: data.type,
        ...(isCompanyAdmin && data.storeId?.trim()
          ? { storeId: data.storeId.trim() }
          : {})
      }) as unknown as CreateCustomerInput;

      await createCustomer.mutateAsync(createCustomerInput);

      if (onCustomerCreated) {
        setListRefreshPending(true);
        try {
          await onCustomerCreated();
        } finally {
          setListRefreshPending(false);
        }
      }

      reset();
      onClose();
    } catch (error) {
      if (error instanceof HandledGraphQLMutationError && error.apiCode) {
        const msg = translateCustomerCreateConflict(t, error.apiCode);
        setSubmitApiError(
          msg ??
            t(
              "create-customer-failed",
              "Could not create customer. Please try again."
            )
        );
        return;
      }
      console.error("Failed to create customer:", error);
    }
  };

  const isLoading =
    isSubmitting || createCustomer.isPending || listRefreshPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: matchMD ? 2 : 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            {t("add-customer", "Add Customer")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: matchMD ? 2 : 3,
            pt: 2,
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "8px"
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px",
              "&:hover": {
                backgroundColor: "#555"
              }
            }
          }}
        >
          {/* Form - Vertical Layout */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            {submitApiError ? (
              <Alert
                severity="error"
                onClose={() => setSubmitApiError(null)}
                sx={{ mb: 0.5 }}
              >
                {submitApiError}
              </Alert>
            ) : null}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("name", "Name")}
                  placeholder={t(
                    "customer-name-placeholder",
                    "Enter customer name"
                  )}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("customer-type", "Customer Type")}
                  error={!!errors.type}
                  helperText={errors.type?.message}
                  disabled={isLoading}
                >
                  {Object.values(CustomerType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {customerType === CustomerType.COMPANY && (
              <Controller
                name="tin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    label={t("tin-label", "TIN (Tax Identification Number)")}
                    placeholder={t("tin-placeholder", "Enter TIN")}
                    error={!!errors.tin}
                    helperText={errors.tin?.message}
                    disabled={isLoading}
                  />
                )}
              />
            )}

            {/* Phone Number with Country Code */}
            <Box
              sx={{
                display: "flex",
                gap: matchMD ? 1 : 2,
                alignItems: "flex-start",
                flexDirection: matchMD ? "column" : "row"
              }}
            >
              <Controller
                name="phoneCountryCode"
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{
                      flex: matchMD ? 1 : "0 0 160px",
                      minWidth: matchMD ? "100%" : 140,
                      width: matchMD ? "100%" : "auto"
                    }}
                    variant="outlined"
                    error={!!errors.phoneCountryCode}
                    disabled={isLoading}
                    size={matchMD ? "small" : "medium"}
                  >
                    <InputLabel id="phone-country-code-label">
                      {t("code", "Code")}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || getDefaultCountryCode()}
                      labelId="phone-country-code-label"
                      id="phone-country-code"
                      label={t("code", "Code")}
                      disabled={isLoading}
                    >
                      {Object.keys(PhoneCountryCodeMap).map((key) => (
                        <MenuItem key={key} value={key}>
                          {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.phoneCountryCode && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.75 }}
                      >
                        {errors.phoneCountryCode.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("phone-number", "Phone Number")}
                    fullWidth
                    size={matchMD ? "small" : "medium"}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder="782484464"
                    disabled={isLoading}
                  />
                )}
              />
            </Box>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size={matchMD ? "small" : "medium"}
                  label={t("email", "Email")}
                  placeholder={t("email-placeholder", "Enter email")}
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Paper
              variant="outlined"
              sx={{
                p: matchMD ? 1.5 : 2,
                display: "flex",
                flexDirection: "column",
                gap: matchMD ? 1.5 : 2
              }}
            >
              <Typography
                variant={matchMD ? "body2" : "subtitle1"}
                fontWeight={600}
                color="textPrimary"
              >
                {t("address", "Address")}
              </Typography>
              <AddressForm
                setValue={setValue}
                control={control}
                defaultAddress={useWatch({ control, name: "address" })}
              />
            </Paper>
          </Box>
        </Box>

        {/* Fixed Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: matchMD ? "column" : "row",
            gap: matchMD ? 1 : 2,
            p: matchMD ? 2 : 3,
            pt: matchMD ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper"
          }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("create-customer", "Create Customer")}
          </LoadingButton>

          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white",
                borderColor: BRAND_HOVER
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCustomerModal;
